import React, { useCallback, useRef } from 'react';
import RSuiteDateInput from 'rsuite/DateInput';

import InputContainer from './InputContainer';
import Label from './Label';
import InfoText from './InfoText';
import InputField from './InputField';
import Textarea from './Textarea';

import styles from './Input.module.scss';

const Input = (props: any) => {
  const dateInputRef = useRef<HTMLInputElement | null>(null);

  // On focus, snap the cursor to the start of the mask. Without this, a tap
  // landing past the last separator drops the user into the year segment, and
  // rsuite silently rejects further digits once the year is full. The
  // synthetic click is what makes rsuite recompute its selected segment from
  // position 0 — setSelectionRange alone leaves rsuite's internal state on
  // whatever segment the original tap hit.
  const handleDateFocus = useCallback(() => {
    requestAnimationFrame(() => {
      const input = dateInputRef.current;
      if (!input) return;
      input.setSelectionRange(0, 0);
      input.click();
    });
  }, []);

  const renderInputField = () => {
    if (props.type === 'date') {
      const dateFormat = props.dateFormat || 'dd/MM/yyyy';
      const dateHint = props.dateFormatHint || 'DD/MM/YYYY';

      return (
        <div className={styles.DateInput}>
          <RSuiteDateInput
            ref={dateInputRef}
            id={props.id}
            placeholder={props.placeholder}
            value={props.value}
            required={props.required}
            onChange={props.onChange}
            onFocus={handleDateFocus}
            format={dateFormat}
          />
          <span className={styles.DateInputHint}>{dateHint}</span>
        </div>
      );
    }

    if (props.multiline) {
      return (
        <Textarea
          id={props.id}
          type={props.type || 'text'}
          name={props.id}
          placeholder={props.placeholder}
          value={props.value}
          onChange={props.onChange}
          maxLength={props.maxLength}
          style={props.styles}
          small={props.small}
          disabled={props.disabled}
          minRows={props.rows}
        />
      );
    }

    if (props.type === 'checkbox') {
      return (
        <div className={styles.Row}>
          <label htmlFor={props.id} className={styles.CheckboxTapTarget}>
            <InputField
              id={props.id}
              type={props.type || 'text'}
              name={props.id}
              placeholder={props.placeholder}
              value={props.value}
              onChange={props.onChange}
              maxLength={props.maxLength}
              style={props.styles}
              small={props.small}
              disabled={props.disabled}
              checked={props.checked}
            />
          </label>
          {props.content && (
            <label className={styles.ContentText} htmlFor={props.id}>
              {props.content}
            </label>
          )}
        </div>
      );
    }

    return (
      <>
        <InputField
          id={props.id}
          type={props.type || 'text'}
          name={props.id}
          placeholder={props.placeholder}
          value={props.value}
          onChange={props.onChange}
          maxLength={props.maxLength}
          style={props.styles}
          small={props.small}
          disabled={props.disabled}
          min={props.min}
          max={props.max}
        />
        {props.content && (
          <label className={styles.ContentText} htmlFor={props.id}>
            {props.content}
          </label>
        )}
      </>
    );
  };

  const renderInfoText = () => {
    if (props.invalid && props.invalidText) {
      return (
        <InfoText error style={{ marginTop: '0.25rem' }}>
          {props.invalidText}
        </InfoText>
      );
    }

    if (props.info) {
      return <InfoText style={{ marginTop: '0.25rem' }}>{props.info}</InfoText>;
    }

    return null;
  };

  return (
    <InputContainer invalid={props.invalid} disabled={props.disabled}>
      {props.label && (
        <Label
          htmlFor={props.id}
          label={props.label}
          required={props.required}
          autofilled={props.autofilled}
        />
      )}
      {props.description && (
        <InfoText style={{ marginBottom: '0.25rem' }}>
          {props.description}
        </InfoText>
      )}
      {renderInputField()}
      {renderInfoText()}
    </InputContainer>
  );
};

export default Input;
